import * as React from 'react';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { TFunction } from 'i18next';
import styled from 'styled-components';

import { PopupNames } from 'core';
import { usePopupStateOperations } from 'core/hooks';
import { PATHS } from 'router/config';

import { RatingStars } from 'atoms';

import { ITeacherUser } from 'types';

import { TableTeachersComponent, IColumn, IRow } from './TableTeachers';

export interface ITableTeachers {
  data: ITeacherUser[];
  loading?: boolean;
  error?: string;
  loadMore: () => void;
  hasMore: boolean;
  /**
   * Служебные поля (контакты, рейтинг, доступ к LMS) — роли карточки
   * преподавателя. Методисту они приходят пустыми (почта `""`, телефон
   * `null`): без колонок телефона, почты и рейтинга из одних «—» и без
   * значка «нет доступа к LMS» на каждой строке.
   */
  showPrivate?: boolean;
  /** «Заявка» / «Редактировать» в строке — роли карточки преподавателя. */
  showRowActions?: boolean;
}

const TEACHING = 'teaching';

/** Колонки служебных полей — без `showPrivate` их нет. */
const PRIVATE_COLUMNS: ReadonlyArray<IColumn['accessor']> = [
  'phone',
  'email',
  'rating',
];

const StatusChip = styled.span<{ $teaching: boolean }>`
  display: inline-block;
  padding: 2px 12px;
  border-radius: 12px;
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
  color: ${({ $teaching }) => ($teaching ? '#1e5eff' : '#0f8a4d')};
  background: ${({ $teaching }) => ($teaching ? '#e8efff' : '#e7f6ee')};
`;

const RatingCell = styled.span`
  display: inline-flex;
  align-items: center;
  color: #5b6775;
`;

const getColumnsSource = (t: TFunction): IColumn[] => [
  {
    Header: `${t('table.teachers.name')}`,
    accessor: 'fullName',
    flexible: true,
  },
  {
    Header: `${t('table.teachers.phone')}`,
    accessor: 'phone',
    columnWidth: '132px',
    marginRight: '64px',
    marginLeft: '64px',
  },
  {
    Header: `${t('table.teachers.email')}`,
    accessor: 'email',
    columnWidth: '208px',
  },
  {
    Header: `${t('table.teachers.status')}`,
    accessor: 'availability',
    columnWidth: '176px',
    marginLeft: '64px',
    Cell: ({ row }) => {
      const isTeaching = row.original.availability === TEACHING;
      return (
        <StatusChip $teaching={isTeaching}>
          {isTeaching
            ? t('table.teachers.availability.teaching')
            : t('table.teachers.availability.free')}
        </StatusChip>
      );
    },
  },
  {
    Header: `${t('table.teachers.rating')}`,
    accessor: 'rating',
    columnWidth: '132px',
    marginLeft: '32px',
    Cell: ({ row }) =>
      row.original.rating ? (
        <RatingStars
          value={row.original.rating}
          size={16}
          ariaLabel={`${t('table.teachers.rating')}`}
        />
      ) : (
        <RatingCell>—</RatingCell>
      ),
  },
  {
    Header: `${t('table.teachers.courses')}`,
    accessor: 'courses',
    flexible: true,
    marginRight: '100px',
    marginLeft: '64px',
  },
];

export const TableTeachers = memo(
  ({
    data,
    loading,
    error,
    loadMore,
    hasMore,
    showPrivate = true,
    showRowActions = true,
  }: ITableTeachers) => {
    const { t } = useTranslation();
    const history = useHistory();
    const columns: IColumn[] = useMemo(
      () =>
        getColumnsSource(t).filter(
          (column) => showPrivate || !PRIVATE_COLUMNS.includes(column.accessor),
        ),
      [t, showPrivate],
    );
    const rows: IRow[] = useMemo(() => data, [data]);

    const {
      operations: { showPopup },
    } = usePopupStateOperations();

    const onEditButtonClick = useCallback(
      (teacherId) => {
        showPopup(PopupNames.teacherEditPopup, { teacherId });
      },
      [showPopup],
    );

    const onClickAddApplication = useCallback(
      (teacherId) => {
        showPopup(PopupNames.appendApplicationPopup, { teacherId });
      },
      [showPopup],
    );

    const onClickOpenDetail = useCallback(
      (teacherId: string) => {
        history.push(PATHS.teacherDetail.replace(':id', teacherId));
      },
      [history],
    );

    if (error) {
      return <p>{error}</p>;
    }

    return (
      <TableTeachersComponent
        rows={rows}
        columns={columns}
        isFlexible
        noDataText={t('table.teachers.noData')}
        loadMore={loadMore}
        hasMore={hasMore}
        onEditButtonClick={onEditButtonClick}
        onClickAddApplication={onClickAddApplication}
        onClickOpenDetail={onClickOpenDetail}
        loading={loading}
        showRowActions={showRowActions}
        showLmsAccess={showPrivate}
      />
    );
  },
);
